import React, { useCallback, useContext, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Transaction } from "@common/types/transaction";
import { Counterparty } from "@common/types/counterparty";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { useShowCounterparty } from "@hooks/useShowCounterparty";
import { Picker } from "@design-system/Picker/Picker.lib";
import { StandardModal } from "@design-system/Modal/Modal";
import { PlusIcon } from "@icons";
import {
    useCounterpartyPickerQuery,
    useCreateCounterpartyMutation,
} from "@api/counterparty.api";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { toPickerLoadMore } from "@helpers/pickerLoadMore";
import { CreateCounterpartyModal } from "@components/counterparty/CreateCounterpartyModal";
import { useCounterpartyVerifiedToast } from "@hooks/useCounterpartyVerifiedToast";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsField } from "./TransactionDetailsField";
import styles from "./TransactionDetailsCounterparty.module.scss";

const FIVE_MINUTES = 5 * 60 * 1000;

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
    lockTooltip?: string;
}

export const TransactionDetailsCounterparty: React.FC<Props> = ({
    transaction,
    isLocked,
    lockTooltip,
}) => {
    const { onUpdate } = useContext(TransactionDetailsContext);
    const activeWorkspace = useActiveWorkspace();
    const showCounterpartyVerifiedToast = useCounterpartyVerifiedToast({
        forTransactionEditing: true,
    });

    const showCounterparty = useShowCounterparty();
    const counterpartyToShow = showCounterparty(transaction.counterparty)
        ? transaction.counterparty
        : null;

    const [typedSearch, setTypedSearch] = useState<string>();
    const [search, setSearch] = useState<string>();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showCreationModal, setShowCreationModal] = useState(false);

    useDebouncedEffect(
        () => {
            setSearch(typedSearch);
        },
        [typedSearch],
        SEARCH_DEBOUNCE_TIME,
    );

    const counterpartiesQuery = useCounterpartyPickerQuery(
        {
            search,
            sort: DirectorySort.NAME_ASC,
            includeRegistered: search
                ? RegisteredCounterpartyIncludeScope.ALL
                : RegisteredCounterpartyIncludeScope.USED,
        },
        {
            staleTime: !search ? FIVE_MINUTES : undefined,
            enabled: !!activeWorkspace && isDropdownOpen,
        },
    );

    const counterparties = useMemo(
        () => counterpartiesQuery.data ?? [],
        [counterpartiesQuery.data],
    );
    const counterpartiesById = useMemo(
        () => new Map(counterparties.map((c) => [c.id, c])),
        [counterparties],
    );

    const updateCounterparty = useMutation({
        mutationFn: async (counterparty: Counterparty | null) => {
            if (counterparty) {
                await onUpdate({ counterpartyId: counterparty.id });
            } else {
                await onUpdate({ counterpartyId: null });
            }
        },
    });

    const { mutate: mutateCounterparty } = updateCounterparty;
    const { mutateAsync: createCounterparty } = useCreateCounterpartyMutation();

    const onCounterpartyCreated = useCallback(
        (creationResponse: CounterpartyCreationResponse) => {
            mutateCounterparty(creationResponse.counterparty);
            showCounterpartyVerifiedToast(creationResponse);
        },
        [mutateCounterparty, showCounterpartyVerifiedToast],
    );

    const onCreateNew = useCallback(
        (typedName: string) => {
            if (typedName) {
                createCounterparty({ name: typedName })
                    .then((res) => {
                        onCounterpartyCreated(res);
                    })
                    .catch(() => {
                        // error is handled by react-query's mutation state
                    });
            } else {
                setShowCreationModal(true);
            }
        },
        [createCounterparty, onCounterpartyCreated],
    );

    const onCreationModalClosed = useCallback(
        (creationResponse?: CounterpartyCreationResponse) => {
            setShowCreationModal(false);
            if (creationResponse) {
                onCounterpartyCreated(creationResponse);
            }
        },
        [onCounterpartyCreated],
    );

    return (
        <>
            <TransactionDetailsField
                label="Counterparty"
                value={
                    <Picker.Select
                        searchable
                        {...toPickerLoadMore(counterpartiesQuery)}
                        value={counterpartyToShow?.id ?? null}
                        open={isDropdownOpen}
                        onValueChange={(counterpartyId) => {
                            if (isLocked) {
                                return;
                            }
                            if (counterpartyId === null) {
                                updateCounterparty.mutate(null);
                                return;
                            }
                            const counterparty =
                                counterpartiesById.get(counterpartyId);
                            if (counterparty) {
                                updateCounterparty.mutate(counterparty);
                            }
                        }}
                        onInputValueChange={(inputValue) => {
                            setTypedSearch(inputValue || undefined);
                        }}
                        onOpenChange={(open) => {
                            setIsDropdownOpen(open);
                        }}
                    >
                        <Picker.ButtonTrigger
                            render={
                                <Picker.Button
                                    variant="primary"
                                    size="md"
                                    disabled={isLocked}
                                    className={styles.counterpartyTrigger}
                                />
                            }
                            value={counterpartyToShow?.name}
                            placeholder="Set counterparty"
                            tooltip={lockTooltip}
                            showChevron={false}
                        />
                        <Picker.Popup sideOffset={4}>
                            <Picker.Search placeholder="Search counterparties..." />
                            <Picker.List>
                                {counterpartyToShow && (
                                    <Picker.Item
                                        onSelect={() => {
                                            if (!isLocked) {
                                                updateCounterparty.mutate(null);
                                            }
                                        }}
                                    >
                                        No counterparty
                                    </Picker.Item>
                                )}
                                {counterpartiesQuery.isLoading && (
                                    <Picker.Loading>Loading…</Picker.Loading>
                                )}
                                {counterparties.map((counterparty) => (
                                    <Picker.Item
                                        key={counterparty.id}
                                        value={counterparty.id}
                                    >
                                        {counterparty.name}
                                    </Picker.Item>
                                ))}
                                {!counterpartiesQuery.isLoading &&
                                    counterparties.length === 0 && (
                                        <Picker.Empty>
                                            No matching counterparties
                                        </Picker.Empty>
                                    )}
                                <Picker.LoadMore />
                                <Picker.Footer>
                                    <Picker.Item
                                        leading={<PlusIcon />}
                                        onSelect={() => {
                                            setIsDropdownOpen(false);
                                            onCreateNew(typedSearch ?? "");
                                        }}
                                    >
                                        {typedSearch
                                            ? `Create "${typedSearch}"`
                                            : "Create new counterparty"}
                                    </Picker.Item>
                                </Picker.Footer>
                            </Picker.List>
                        </Picker.Popup>
                    </Picker.Select>
                }
            />
            <StandardModal
                show={showCreationModal}
                onHide={onCreationModalClosed}
                size="lg"
            >
                <CreateCounterpartyModal
                    close={onCreationModalClosed}
                    forTransactionEditing
                />
            </StandardModal>
        </>
    );
};
